import { Material, Mesh, Texture, type Object3D } from 'three'

export interface DisposedObjectResources {
  geometries: Set<Mesh['geometry']>
  materials: Set<Material>
  textures: Set<Texture>
}

const collectResources = (root: Object3D): DisposedObjectResources => {
  const geometries = new Set<Mesh['geometry']>()
  const materials = new Set<Material>()
  const textures = new Set<Texture>()

  root.traverse((object) => {
    if (!(object instanceof Mesh)) return
    geometries.add(object.geometry)
    const entries = Array.isArray(object.material) ? object.material : [object.material]
    entries.forEach((material) => materials.add(material))
  })

  materials.forEach((material) => {
    Object.values(material).forEach((value) => {
      if (value instanceof Texture) textures.add(value)
    })
  })

  return { geometries, materials, textures }
}

/** Dispose resources used only by root, preserving anything shared by retainedRoot. */
export const disposeObject3D = (
  root: Object3D,
  retainedRoot?: Object3D,
): DisposedObjectResources => {
  const candidates = collectResources(root)
  const retained = retainedRoot ? collectResources(retainedRoot) : null
  const disposed: DisposedObjectResources = {
    geometries: new Set(),
    materials: new Set(),
    textures: new Set(),
  }

  candidates.materials.forEach((material) => {
    if (retained?.materials.has(material)) return
    Object.values(material).forEach((value) => {
      if (
        value instanceof Texture
        && value.userData.runtime_shared_texture !== true
        && !retained?.textures.has(value)
      ) {
        disposed.textures.add(value)
      }
    })
    material.dispose()
    disposed.materials.add(material)
  })
  disposed.textures.forEach((texture) => texture.dispose())
  candidates.geometries.forEach((geometry) => {
    if (retained?.geometries.has(geometry)) return
    geometry.dispose()
    disposed.geometries.add(geometry)
  })

  return disposed
}
